import { AddOnTextInput, Button, Icon, Loading, Toggle, Typography } from "@/components/atoms";
import { DefaultRatesFormData, defaultRatesFormSchema, HowPricingWorksButton, HowPricingWorksModal } from "@/components/organisms";
import { useGetBillingSettingsQuery, useUpdateBillingSettingsMutation } from "@/redux/apis/config/business/billingSettings";
import { useAppSelector } from "@/redux/hooks";
import { billingSettingsSelector } from "@/redux/slices/business/selectors";
import { useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { addToast, clsx } from "@/utils";

export const DefaultRates = () => {
  const [openModal, setOpenModal] = useState(false);
  const { isFetching } = useGetBillingSettingsQuery();
  const [updateBillingSettings, { isLoading: isLoadingSettings }] = useUpdateBillingSettingsMutation();
  const isLoading = isFetching || isLoadingSettings;

  const {
    details: { defaultUniversalPricingAdjustmentRate },
  } = useAppSelector(billingSettingsSelector);

  const defaultRate = Number(defaultUniversalPricingAdjustmentRate);

  // Map server value to defaults
  const defaultValues = useMemo<DefaultRatesFormData>(() => {
    if (defaultRate > 0) return { mode: "raise", raisePricing: Math.abs(defaultRate), lowerPricing: 0 };
    if (defaultRate < 0) return { mode: "lower", lowerPricing: Math.abs(defaultRate), raisePricing: 0 };
    return { mode: "default", raisePricing: 0, lowerPricing: 0 };
  }, [defaultRate]);

  const {
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<DefaultRatesFormData>({
    defaultValues,
    resolver: zodResolver(defaultRatesFormSchema),
  });

  const mode = watch("mode");
  const isRaise = mode === "raise";
  const isLower = mode === "lower";

  // Local inputs (so user can clear field while typing)
  const [raiseInput, setRaiseInput] = useState<string>(String(defaultValues.raisePricing));
  const [lowerInput, setLowerInput] = useState<string>(String(defaultValues.lowerPricing));

  // Reset when defaults change
  useEffect(() => {
    reset(defaultValues);
    setRaiseInput(String(defaultValues.raisePricing));
    setLowerInput(String(defaultValues.lowerPricing));
  }, [defaultValues, reset]);

  // Show validation errors as toast
  useEffect(() => {
    const m = errors.raisePricing?.message || errors.lowerPricing?.message;
    if (m) addToast("danger", m);
  }, [errors]);

  // Toggle logic
  const setMode = (next: "default" | "raise" | "lower") => {
    setValue("mode", next, { shouldDirty: true, shouldValidate: false });
  };
  const onToggleRaise = (checked: boolean) => setMode(checked ? "raise" : isLower ? "lower" : "default");
  const onToggleLower = (checked: boolean) => setMode(checked ? "lower" : isRaise ? "raise" : "default");

  // Input handling
  const onlyInt0to9999 = (v: string) => /^\d{0,4}$/.test(v);

  const handleRaiseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.trim();
    if (v === "" || onlyInt0to9999(v)) setRaiseInput(v);
  };
  const handleLowerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.trim();
    if (v === "" || onlyInt0to9999(v)) setLowerInput(v);
  };

  const commitRaise = () => {
    const n = Number(raiseInput);
    const val = Number.isFinite(n) ? n : 0;
    setValue("raisePricing", val, { shouldDirty: true, shouldValidate: true });
    setRaiseInput(String(val));
  };
  const commitLower = () => {
    const n = Number(lowerInput);
    const val = Number.isFinite(n) ? n : 0;
    setValue("lowerPricing", val, { shouldDirty: true, shouldValidate: true });
    setLowerInput(String(val));
  };

  // Detect changes vs defaults
  const hasChanges = useMemo(() => {
    const r = Number(raiseInput || 0);
    const l = Number(lowerInput || 0);
    return mode !== defaultValues.mode || r !== defaultValues.raisePricing || l !== defaultValues.lowerPricing;
  }, [mode, raiseInput, lowerInput, defaultValues]);

  // Submit
  const onSubmit = handleSubmit(async (data) => {
    const value =
      data.mode === "raise"
        ? Math.abs(data.raisePricing)
        : data.mode === "lower"
        ? -Math.abs(data.lowerPricing)
        : 0;

    updateBillingSettings({
      default_universal_pricing_adjustment_rate: value.toString(),
    })
      .unwrap()
      .then(() => addToast("success", "Successfully Updated the Default Rates"))
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  });

  // Status banner
  const renderStatusBanner = () => {
    const base = "flex flex-col items-center justify-center rounded-lg border px-4 py-3 w-full max-w-[600px]";
    if (isLoading) return null;

    if (defaultValues.mode === "default") {
      return (
        <div className={clsx(base, "border-info bg-info-light")}>
          <Typography variant="paragraph" className="text-info">
            Your pricing is set to the system&apos;s default rates.
          </Typography>
        </div>
      );
    }

    const isDefaultRaise = defaultValues.mode === "raise";
    const pct = isDefaultRaise ? defaultValues.raisePricing : defaultValues.lowerPricing;

    return (
      <div
        className={clsx(base, {
          "border-success bg-success-light": isDefaultRaise,
          "border-danger bg-danger-light": !isDefaultRaise,
        })}
      >
        <Typography
          variant="paragraph"
          className={clsx({
            "text-success": isDefaultRaise,
            "text-danger": !isDefaultRaise,
          })}
        >
          Your default pricing is currently set to be <b>{pct}% {isDefaultRaise ? "higher" : "lower"}</b> than the system&apos;s default rates.
        </Typography>
      </div>
    );
  };

  return (
    <div className="flex flex-1 flex-col min-h-[calc(100vh-120px)] px-5 py-4">
      <div className="flex flex-row items-center justify-end">
        <HowPricingWorksButton onClick={() => setOpenModal(true)} />
      </div>

      <form onSubmit={onSubmit}>
        <div className="relative flex flex-col items-center text-center mt-2">
          <div className="flex items-center justify-center p-2 rounded-full bg-primary-light">
            <Icon isCustom name="speedtest" size={48} className="flex-shrink-0" />
          </div>
          <Typography variant="title" className="text-neutral-dark-gray mt-5">
            Default Rates
          </Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray mt-2 mb-5 max-w-[500px]">
            Raise or lower all default prices by a percentage. This won&apos;t change any custom prices you&apos;ve set up.
          </Typography>

          {renderStatusBanner()}

          <div className="relative flex flex-col gap-y-3 w-full max-w-[600px] mt-6">
            {isLoading && <Loading />}

            {/* Raise */}
            <div className="flex flex-col md:flex-row md:items-center rounded-lg bg-white gap-3 p-4">
              <Toggle size="sm" checked={isRaise} onChange={onToggleRaise} />
              <div className="flex flex-row items-center justify-between gap-x-4 w-full">
                <div className="flex flex-row items-center gap-x-1.5">
                  <Icon isCustom name="chevron-up" size="md" className="flex-shrink-0" />
                  <Typography variant="title" className="text-neutral-dark-gray">Raise Default Prices by</Typography>
                </div>
                <AddOnTextInput
                  type="text"
                  inputMode="numeric"
                  className="w-[100px]"
                  endAddOn="%"
                  size="md"
                  value={raiseInput}
                  onChange={handleRaiseChange}
                  onBlur={commitRaise}
                  disabled={!isRaise}
                />
              </div>
            </div>

            {/* Lower */}
            <div className="flex flex-col md:flex-row md:items-center rounded-lg bg-white gap-3 p-4">
              <Toggle size="sm" checked={isLower} onChange={onToggleLower} className="bg-danger" />
              <div className="flex flex-row items-center justify-between gap-x-4 w-full">
                <div className="flex flex-row items-center gap-x-1.5">
                  <Icon isCustom name="chevron-down" size="md" className="flex-shrink-0" />
                  <Typography variant="title" className="text-neutral-dark-gray">Lower Default Prices by</Typography>
                </div>
                <AddOnTextInput
                  type="text"
                  inputMode="numeric"
                  className="w-[100px]"
                  endAddOn="%"
                  size="md"
                  value={lowerInput}
                  onChange={handleLowerChange}
                  onBlur={commitLower}
                  disabled={!isLower}
                />
              </div>
            </div>
          </div>

          {hasChanges && (
            <div className="flex flex-col gap-3 mt-6">
              <Button type="submit" variant="primary" size="lg" disabled={isLoading}>
                Save Changes
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="lg"
                onClick={() => {
                  reset(defaultValues);
                  setRaiseInput(String(defaultValues.raisePricing));
                  setLowerInput(String(defaultValues.lowerPricing));
                }}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </form>

      <HowPricingWorksModal open={openModal} onClose={() => setOpenModal(false)} />
    </div>
  );
};